import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Button } from '@/shared/components';
import { problemText, useApi, useApiProblem, type BookingResponse } from '@/shared/api';
import { formatMoney } from '@/shared/lib/format';
import { majorFromMinor } from '@/shared/lib/money';
import { channelName } from './helpers';
import { OtaRefundDialog } from './OtaRefundDialog';
import { OtaRefundHistory } from './OtaRefundHistory';
import { SectionCard } from './SectionCard';
import { useOtaRefundCommands } from './useOtaRefundCommands';

export function OtaRefundPanel({ booking }: { booking: BookingResponse }) {
  const { t } = useTranslation('otaRefunds');
  const api = useApi();
  const ledger = useQuery({
    queryKey: ['ota-refunds', booking.id],
    queryFn: () => api.otaRefunds.get(booking.id),
    enabled: booking.source === 'Ota' && booking.priceEvidenceState === 'Known',
    retry: false,
  });
  const refund = useOtaRefundCommands(booking.id, ledger.data);
  const problem = useApiProblem(ledger.error);
  const failureText =
    problem?.title === 'ota_refund_financial_context_missing'
      ? t('missing')
      : problem?.title === 'ota_refund_financial_context_invalid'
        ? t('conflict')
        : problemText(problem, 'Die Erstattungen konnten nicht geladen werden.');
  const title = t('title', { channel: channelName(booking.channel) });

  if (booking.source !== 'Ota') return null;
  if (booking.priceEvidenceState !== 'Known') {
    return (
      <SectionCard icon="Receipt" title={title}>
        <p className="text-ink-soft text-sm">
          Der Preis vom Kanal wird noch geprüft. Erstattungen bleiben bis dahin gesperrt.
        </p>
      </SectionCard>
    );
  }
  if (ledger.isLoading) {
    return (
      <SectionCard icon="Receipt" title={title}>
        <p className="text-ink-soft text-sm">Erstattungen werden geladen…</p>
      </SectionCard>
    );
  }
  // Vorher verschwand die Karte bei einem Fehler still – samt der Möglichkeit zu erstatten.
  if (!ledger.data) {
    return (
      <SectionCard icon="Receipt" title={title}>
        <p role="alert" className="text-ink-soft text-sm">
          {failureText}
        </p>
        <Button
          variant="ghost"
          className="min-h-11 self-start"
          loading={ledger.isFetching}
          onClick={() => void ledger.refetch()}
        >
          Erneut laden
        </Button>
      </SectionCard>
    );
  }

  const data = ledger.data;
  return (
    <SectionCard icon="Receipt" title={title}>
      {ledger.isError && (
        <p role="alert" className="text-ink-soft text-sm">
          {failureText} {t('stale')}
        </p>
      )}
      {data.items.length === 0 && <p className="text-ink-soft text-sm">{t('empty')}</p>}
      <div className="grid gap-3 sm:grid-cols-3">
        <Metric
          label="Dokumentiert erstattet"
          value={formatMoney(
            majorFromMinor(data.effectiveRefundedMinor, data.currency),
            data.currency,
          )}
        />
        <Metric
          label="Verbleibend"
          value={formatMoney(
            majorFromMinor(data.remainingGrossMinor, data.currency),
            data.currency,
          )}
        />
        <div className="flex items-end sm:justify-end">
          <Button
            variant="ghost"
            className="min-h-11 w-full sm:w-auto"
            onClick={() => refund.setOpen(true)}
            disabled={data.remainingGrossMinor === 0}
          >
            Erstattung dokumentieren
          </Button>
        </div>
      </div>
      <OtaRefundHistory items={data.items} onCompensate={refund.startCompensation} />

      <OtaRefundDialog
        open={refund.open}
        onOpenChange={refund.setOpen}
        mode={refund.mode}
        ledger={data}
        amount={refund.amount}
        category={refund.category}
        reason={refund.reason}
        pending={refund.pending}
        canSubmit={refund.canSubmit}
        onAmountChange={refund.changeAmount}
        onCategoryChange={refund.changeCategory}
        onReasonChange={refund.changeReason}
        onSubmit={refund.submit}
      />
    </SectionCard>
  );
}

function Metric({ label, value }: { label: string; value: string }) {
  return (
    <div className="bg-paper rounded-control px-4 py-3">
      <p className="text-ink-soft text-xs">{label}</p>
      <p className="mt-1 font-mono text-sm font-semibold tabular-nums">{value}</p>
    </div>
  );
}
